import { encodeSymbolPath } from '../../core/bridge/pathenc';
import type { BackendClient } from './backend-client';

/*
 * downloadHistoryCsv — the History tab's "export what's on screen" download,
 * against the api-gateway's raw /v1/history/export.csv route.
 *
 * Main-thread on purpose: this is the one IO shape that CANNOT ride the
 * Backend Worker, because the destination is the user's disk — the save
 * picker and the anchor click only exist here. The component still never
 * fetches: it calls this helper, which gets the gateway base URL from the
 * worker and the Clerk token from the client, same as the camera recording
 * download (vitesse-camera-detail.ts) this is modeled on.
 *
 * The token and gateway id ride the fetch HEADERS — an href download can't
 * carry them, and the token must NOT go in the URL (URLs land in history,
 * logs, and proxies). On Chromium the response streams straight to disk via
 * the File System Access API, so a large export never exists in RAM here;
 * elsewhere it falls back to blob + anchor.
 *
 * Failures before the first byte arrive as prose in the response body (the
 * route writes human sentences, not bare codes) — thrown as the Error message
 * so the dialog can show it verbatim.
 *
 * Returns false (not an error) when the user dismisses the Chromium save
 * picker — that's a change of mind, not a failure, so a caller distinguishing
 * "succeeded" from "failed" feedback must not conflate it with either.
 */
export async function downloadHistoryCsv(
  backend: BackendClient,
  gatewayId: string,
  controllerId: string,
  symbols: readonly string[],
  fromMs: number,
  toMs: number,
): Promise<boolean> {
  const base = await backend.api.gatewayHttpBase(gatewayId);
  // Scope decides the symbol dialect — the SAME rule as connect-client's
  // queryRange: CONTROLLER scope stores encoded subject tails (the historian
  // never saw the display path, so sending it matches zero rows, silently),
  // while MODEL scope stores variable ids verbatim and must NOT be encoded
  // (stage_num → stage__num is a symbol that was never stored).
  const isModelScope = controllerId.startsWith('model:');
  const params = new URLSearchParams({
    controller_id: controllerId,
    symbols: (isModelScope ? symbols : symbols.map(encodeSymbolPath)).join(','),
    // ms → ns without float rounding: 2^53 ns is ~104 days from epoch, so a
    // Number multiply would corrupt any modern timestamp.
    from_ns: (BigInt(Math.floor(fromMs)) * 1_000_000n).toString(),
    to_ns: (BigInt(Math.ceil(toMs)) * 1_000_000n).toString(),
  });
  const url = `${base}/v1/history/export.csv?${params}`;

  const headers: Record<string, string> = { 'X-Vitesse-Gateway-Id': gatewayId };
  const token = await backend.getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const fname = `history_${controllerId || 'all'}_${new Date(fromMs).toISOString().slice(0, 19)}.csv`;

  // Read the route's prose body off a refused response so the dialog shows
  // the reason ("your organization's plan does not include…"), not a number.
  const refusal = async (res: Response): Promise<Error> => {
    const body = (await res.text().catch(() => '')).trim();
    return new Error(body || `export failed (HTTP ${res.status})`);
  };
  // A 200 that isn't CSV means the request never reached the export route —
  // e.g. a dev/SPA fallback answering with index.html. Saving that as .csv
  // would be a silent lie; refuse it with a routable message instead.
  const notCsv = (res: Response): boolean =>
    !(res.headers.get('content-type') ?? '').toLowerCase().includes('text/csv');
  const misrouted = () =>
    new Error('export route not reachable (response was not CSV) — is /v1/history/ proxied to the api-gateway?');

  // Preferred: stream to disk (no buffering). Chromium-only — falls back below.
  const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> })
    .showSaveFilePicker;
  if (typeof picker === 'function') {
    let handle: FileSystemFileHandle;
    try {
      handle = await picker({ suggestedName: fname, types: [{ accept: { 'text/csv': ['.csv'] } }] });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return false; // user dismissed the save dialog
      throw e;
    }
    const writable = await handle.createWritable();
    const res = await fetch(url, { headers });
    if (!res.ok || !res.body) {
      await writable.close().catch(() => undefined);
      throw await refusal(res);
    }
    if (notCsv(res)) {
      await writable.close().catch(() => undefined);
      throw misrouted();
    }
    await res.body.pipeTo(writable); // streams to disk + closes the file on completion
    return true;
  }

  // Fallback (non-Chromium): buffer then anchor-download.
  const res = await fetch(url, { headers });
  if (!res.ok) throw await refusal(res);
  if (notCsv(res)) throw misrouted();
  const blobUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return true;
}
